import React from 'react'
import { Helmet } from 'react-helmet-async'
import Landing from '../../Esg/Landing';
import { FaAngleRight } from 'react-icons/fa';
import EsgCard from '../../Esg/EsgCard';
import WhyNirantara from './WhyNirantara';
import ExploreSol from './ExploreSol';
const NIRANTARAETHOS = [
    {
        "title": "Sustainability as a Core Value",
        "description": "Nirantara places sustainability at the heart of its operations, creating solutions that balance economic growth with environmental and social responsibility. By guiding clients to integrate ESG principles into their strategies, the company drives long-term, sustainable growth while making a positive impact on society and the planet.",
        "image": "/assets/images/about/about-1.png"
    },
    {
        "title": "Integrity and Transparency",
        "description": "Trust is paramount in all of Nirantara's relationships with clients, partners, employees, and communities. The company upholds the highest standards of integrity and ethics, conducting business with honesty and transparency. Through open communication and clear decision-making, Nirantara fosters lasting trust and credibility with all stakeholders.",
        "image": "/assets/images/about/about-2.png"
    },
    {
        "title": "Innovation and Adaptability",
        "description": "In the dynamic world of sustainability, Nirantara embraces innovation and adaptability as drivers of success. By investing in research, staying ahead of trends, and fostering creative thinking, the company delivers cutting-edge solutions that help clients navigate the evolving challenges of sustainable business.",
        "image": "/assets/images/about/about-3.png"
    },
    {
        "title": "Collaboration and Partnership",
        "description": "Nirantara understands that global sustainability challenges require collective action. By fostering collaboration with clients, industry peers, NGOs, and communities, the company leverages partnerships to amplify impact and drive a more sustainable future for all.",
        "image": "/assets/images/about/about-4.png"
    },
    {
        "title": "Commitment to Excellence",
        "description": "At Nirantara, excellence is a continuous journey. Committed to delivering top-quality service, the company continuously improves, exceeding expectations through rigorous project management, client-focused solutions, and a relentless pursuit of leadership in sustainability consultancy.",
        "image": "/assets/images/about/about-5.png"
    },
    {
        "title": "Empowerment and Development",
        "description": "Nirantara is committed to empowering its employees and the communities it serves. By fostering an inclusive, diverse work environment and investing in team development and community initiatives, the company nurtures talent, drives growth, and strengthens society.",
        "image": "/assets/images/about/about-6.png"
    }
]

function AboutView() {
    return (
        <>
            <Helmet>
                <title>About Nirantara - ESG & Sustainability Consulting Partner</title>
                <meta name="description" content="Learn about Nirantara's mission, vision, and values, driving ESG and sustainability transformation. Discover how we help organisations achieve net-zero goals, measurable impact, and long-term responsible growth." />
                <meta name="keywords" content="about Nirantara, ESG consulting company, sustainability consulting firm, net zero advisory services, ESG transformation partner, environmental social governance consulting, sustainable business solutions, ESG mission, vision, values" />
                <link rel="canonical" href="https://nirantara.solutions/about-us" />
            </Helmet>
            <Landing image={"/assets/images/nirantara-journey.jpg"} text={"Nirantara Journey"} button={{
                text: "Start Your ESG Journey",
                action: () => {
                    window.location.href = "/contact-us"
                },
                icon: <FaAngleRight />
            }} />
            <section className="flex flex-col items-center w-full gap-10 sm:gap-0 px-[2rem] py-[2rem] sm:px-[5rem] md:px-[5rem] lg:px-[15rem] montserrat bg-[#FFF]">
                <div className="flex flex-col-reverse md:grid md:grid-cols-1">
                    <div className="flex flex-col justify-between w-full py-0 mx-auto text-white md:py-10">
                        <h1 className="text-left text-[#000000] text-3xl sm:text-4xl font-extrabold my-4 sm:mt-10">
                            Empowering Sustainable Change, Continuously
                        </h1>
                        <p className="mb-4 text-[#000000] text-lg leading-10 text-justify">
                            Nirantara, derived from the Sanskrit word for "continuous," embodies our philosophy of persistence, consistency, and forward-thinking action in the realm of sustainability. Our mission is to help businesses, organizations, and communities integrate sustainable practices into their core operations - creating a long-lasting, measurable impact on the environment, society, and governance practices.
                            Founded with the goal of bridging the gap between ESG principles and practical business application, Nirantara is a one-stop solution provider for enterprises looking to achieve environmental responsibility while maintaining profitability.
                        </p>
                    </div>
                </div>
            </section>
            <section className="flex flex-col items-center w-full gap-10 sm:gap-0 px-[2rem] py-[2rem] sm:px-[5rem] md:px-[5rem] lg:px-[15rem] montserrat bg-[#FFF]">
                <div className="flex flex-col md:grid md:grid-cols-2 md:items-stretch md:gap-6 w-full">
                    <div className='flex justify-center items-center w-full my-8 md:my-0 overflow-hidden md:h-auto'>
                        <img src="/assets/images/vision.jpg" alt="Vision" className="w-full h-auto md:max-h-[350px] object-contain rounded-lg" />
                    </div>
                    <div className="flex flex-col justify-center w-full py-0 md:py-10 md:px-6">
                        <h2 className="text-left !leading-snug text-3xl sm:text-4xl font-extrabold text-[#000000] my-4 md:mt-0">
                            Vision
                        </h2>
                        <p className="mb-4 text-[#000000] text-lg leading-10 text-justify">
                            At Nirantara, we aspire to be the preferred partner for sustainable change by delivering integrated, single-window solutions that make sustainability practical, measurable, and impactful. We envision a future where businesses see sustainability not just as compliance, but as a pathway to growth and resilience - enabling organizations to prosper responsibly, communities to thrive, and profitability to coexist with purpose in harmony with the planet.
                        </p>
                    </div>
                </div>
            </section>
            <section className="flex flex-col items-center w-full gap-10 sm:gap-0 px-[2rem] py-[2rem] sm:px-[5rem] md:px-[5rem] lg:px-[15rem] montserrat bg-[#FFF]">
                <div className="flex flex-col-reverse md:grid md:grid-cols-2 md:items-stretch md:gap-6 w-full">
                    <div className="flex flex-col justify-center w-full py-0 md:py-10 md:px-6">
                        <h2 className="text-left !leading-snug text-3xl sm:text-4xl font-extrabold text-[#000000] my-4 md:mt-0">
                            Mission
                        </h2>
                        <p className="mb-4 text-[#000000] text-lg leading-10 text-justify">
                            Nirantara mission is to demystify sustainability by making it accessible and feasible for enterprises across all business streams irrespective of the size. The Nirantara team will strive to promote awareness of sustainability frameworks while providing cost-effective solutions and services for enterprises to easily adopt sustainable practices in their operations. Nirantara aims to be a one-stop-shop solution provider for businesses across different spectrums who aim to achieve Net-Zero status and optimized social balance within the organization.
                        </p>
                    </div>
                    <div className='flex justify-center items-center w-full my-8 md:my-0 overflow-hidden md:h-auto'>
                        <img src="/assets/images/mission.jpg" alt="Mission" className="w-full h-auto md:max-h-[350px] object-contain rounded-lg" />
                    </div>
                </div>
            </section>
            <div
                className="flex flex-col items-center w-full gap-10 sm:gap-14 px-[2rem] py-[2rem] sm:px-[5rem] md:px-[5rem] lg:px-[15rem]  montserrat bg-[#ECECEC]">
                <h5 className='font-bold text-center text-black text-[2.2rem] sm:pt-3'>Nirantara Ethos</h5>
                <p className="mb-4 text-[#000000] text-lg leading-10 text-center px-5">
                    Nirantara is founded on a set of core principles and values that guide its vision, mission, operations, and interactions with clients, communities, and the environment. The ethos of Nirantara outlines the commitment to sustainability, integrity, innovation, collaboration, and excellence. The principle pillars of Nirantara ethos are as follows:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 w-full max-w-[1400px]">
                    {NIRANTARAETHOS
                        && NIRANTARAETHOS.length > 0 && NIRANTARAETHOS.map((item, index) => {
                            const { title, subtitle, description, cta, image } = item;
                            return (
                                <EsgCard
                                    minHeight="100%"
                                    title={title}
                                    subtitle={subtitle}
                                    description={description}
                                    cta={cta}
                                    Icon={image}
                                    key={index}
                                    className={"w-full"}
                                />
                            )
                        })}
                </div>
            </div>
            <WhyNirantara />
            <ExploreSol />
        </>
    )
}

export default AboutView